import { useEffect, useRef, useState } from "react";

type Quote = {
  text: string;
  author: string;
  role: string;
  portrait: string;
};

const QUOTES: Quote[] = [
  {
    text:
      "We don't just write code — we build products people use every day. Speed, transparency, and efficiency are what drive us.",
    author: "Intellivex Team",
    role: "IT Development & Marketing",
    portrait:
      "https://readdy.ai/api/search-image?query=Modern%20bright%20open%20plan%20tech%20office%20interior%20with%20diverse%20team%20of%20developers%20working%20collaboratively%20at%20standing%20desks%20large%20windows%20natural%20daylight%20clean%20minimalist%20design%20warm%20wooden%20accents%20plants%20scattered%20around%20professional%20creative%20atmosphere%20high%20detail&width=680&height=880&seq=intellivex-quote-01&orientation=portrait",
  },
  {
    text:
      "Every app is more than just lines of code. It's a solution to a real user problem. We build utilities that make life simpler.",
    author: "Intellivex Team",
    role: "Utility App Development",
    portrait:
      "https://storage.readdy-site.link/project_files/c0e0600e-4e78-44ef-81fe-aa0be702df4c/0396364d-cd91-41fe-8104-1dcfda2c3287_compressed_ChatGPT-Image-13-.-2026-.-13_39_56.webp",
  },
  {
    text:
      "From ASO to media buying — we know how to bring users to your app and turn them into a loyal audience.",
    author: "Intellivex Team",
    role: "Digital Marketing & Promotion",
    portrait:
      "https://readdy.ai/api/search-image?query=Candid%20documentary%20photograph%20of%20a%20small%20marketing%20team%20in%20a%20modern%20loft%20office%20reviewing%20printed%20analytics%20reports%20and%20sticky%20notes%20on%20a%20glass%20wall%20shot%20on%20Canon%20EOS%20R5%20with%2085mm%20lens%20shallow%20depth%20of%20field%20natural%20window%20light%20warm%20film%20grain%20authentic%20unposed%20moment%20genuine%20human%20interaction%20editorial%20quality&width=680&height=880&seq=intellivex-quote-03-v2&orientation=portrait",
  },
];

export default function QuoteSection() {
  const [index, setIndex] = useState(0);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const [hovering, setHovering] = useState(false);
  const portraitRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-advance
  useEffect(() => {
    if (hovering) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % QUOTES.length);
    }, 6500);
    return () => window.clearInterval(id);
  }, [hovering]);

  const goPrev = () =>
    setIndex((i) => (i - 1 + QUOTES.length) % QUOTES.length);
  const goNext = () => setIndex((i) => (i + 1) % QUOTES.length);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = portraitRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 10, ry: px * 12 });
  };

  const current = QUOTES[index];
  const label = `0${index + 1}`;

  return (
    <section
      ref={sectionRef}
      className="relative w-full"
      style={{
        background: "#000",
        padding: "60px 16px 80px",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 1440 }}>
        <div
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => {
            setHovering(false);
            setTilt({ rx: 0, ry: 0 });
          }}
          className="flex flex-col lg:grid gap-0 lg:gap-6 relative overflow-hidden"
          style={{
            gridTemplateColumns: "minmax(260px, 340px) 1fr",
            background: "#DBEAFE",
            borderRadius: 20,
            padding: "24px 20px",
            color: "#0B1B4D",
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(40px)",
            transition:
              "opacity 0.9s cubic-bezier(0.2, 0.8, 0.2, 1), transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        >
          {/* Giant background quote mark */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: -30,
              right: -10,
              fontSize: "clamp(180px, 30vw, 460px)",
              lineHeight: 1,
              fontWeight: 700,
              color: "#0B1B4D",
              opacity: 0.06,
              pointerEvents: "none",
              userSelect: "none",
              fontFamily: "Georgia, serif",
              transition: "transform 0.6s ease",
              transform: hovering ? "scale(1.06) rotate(-3deg)" : "scale(1)",
            }}
          >
            &ldquo;
          </div>

          {/* Portrait with 3D tilt & crossfade */}
          <div
            ref={portraitRef}
            onMouseMove={handleMove}
            className="relative overflow-hidden w-full lg:w-auto"
            style={{
              borderRadius: 14,
              background: "#93C5FD",
              minHeight: 220,
              height: "220px",
              perspective: 900,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) scale(${
                  hovering ? 1.05 : 1
                })`,
                transformStyle: "preserve-3d",
                transition:
                  "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
              }}
            >
              {QUOTES.map((q, i) => (
                <img
                  key={q.portrait}
                  src={q.portrait}
                  alt={q.author}
                  className="w-full h-full"
                  style={{
                    position: "absolute",
                    inset: 0,
                    objectFit: "cover",
                    objectPosition: "top",
                    opacity: i === index ? 1 : 0,
                    transform:
                      i === index ? "scale(1)" : "scale(1.08)",
                    transition:
                      "opacity 0.8s ease, transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1)",
                  }}
                />
              ))}
              {/* subtle shine */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)",
                  transform: `translateX(${hovering ? 30 : -60}%)`,
                  transition: "transform 1.2s ease",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          {/* Quote content */}
          <div
            className="flex flex-col justify-between relative"
            style={{ minHeight: 280 }}
          >
            <div>
              <div
                key={label}
                style={{
                  fontSize: "clamp(28px, 5vw, 44px)",
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                  animation: "qCounter 0.6s ease",
                }}
              >
                {label}
                <span
                  style={{
                    fontSize: "clamp(16px, 2vw, 20px)",
                    fontWeight: 500,
                    opacity: 0.45,
                    marginLeft: 6,
                  }}
                >
                  / 0{QUOTES.length}
                </span>
              </div>

              <div
                style={{
                  marginTop: 24,
                  position: "relative",
                  minHeight: 120,
                }}
              >
                {QUOTES.map((q, i) => (
                  <p
                    key={i}
                    style={{
                      position: "absolute",
                      inset: 0,
                      fontSize: "clamp(18px, 2.4vw, 34px)",
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                      fontWeight: 500,
                      maxWidth: 720,
                      margin: 0,
                      opacity: i === index ? 1 : 0,
                      transform:
                        i === index
                          ? "translateY(0)"
                          : "translateY(14px)",
                      transition:
                        "opacity 0.55s ease, transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)",
                      pointerEvents: i === index ? "auto" : "none",
                    }}
                  >
                    &ldquo;{q.text}&rdquo;
                  </p>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mt-8 lg:mt-12 relative gap-4 sm:gap-0">
              <div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    transition: "opacity 0.4s ease",
                  }}
                  key={current.author + index}
                >
                  {current.author}
                </div>
                <div
                  style={{ fontSize: 14, color: "#4A5B8C" }}
                  key={current.role + index}
                >
                  {current.role}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Progress dots */}
                <div className="flex items-center gap-1 mr-2">
                  {QUOTES.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIndex(i)}
                      aria-label={`Go to quote ${i + 1}`}
                      className="cursor-pointer"
                      style={{
                        width: i === index ? 24 : 8,
                        height: 8,
                        borderRadius: 9999,
                        background:
                          i === index ? "#0B1B4D" : "rgba(11,27,77,0.25)",
                        border: "none",
                        transition:
                          "width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), background 0.3s",
                      }}
                    />
                  ))}
                </div>
                <ArrowBtn direction="prev" onClick={goPrev} />
                <ArrowBtn direction="next" onClick={goNext} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes qCounter {
          from { opacity: 0.2; transform: translateY(-6px); }
          to   { opacity: 1;   transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

function ArrowBtn({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick?: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="whitespace-nowrap cursor-pointer flex items-center justify-center rounded-full relative overflow-hidden"
      style={{
        width: 44,
        height: 44,
        background: "#0B1B4D",
        color: "#DBEAFE",
        border: "none",
        transition: "transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)",
        transform: hover ? "scale(1.08)" : "scale(1)",
      }}
      aria-label={direction === "prev" ? "Previous" : "Next"}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "9999px",
          background: "#3B82F6",
          transform: hover ? "scale(1)" : "scale(0)",
          transition: "transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      />
      <i
        className={
          direction === "prev" ? "ri-arrow-left-line" : "ri-arrow-right-line"
        }
        style={{
          fontSize: 18,
          position: "relative",
          transform: hover
            ? direction === "next"
              ? "translateX(3px)"
              : "translateX(-3px)"
            : "translateX(0)",
          transition: "transform 0.3s ease",
        }}
      />
    </button>
  );
}